import type { Actor, Artifact, ProjectState } from "@minga/core"
import {
  artifactByRef,
  docActorName,
  docByline,
  docPathOf,
  documentHeads,
  isDocument,
  versionsOf,
  viewOf,
} from "@minga/core"
import {
  claudeArtifactOf,
  type DocKind,
  docFolderOf,
  docHomeOf,
  docKindOf,
  docPathKey,
  docUrlHost,
  isProposalPath,
  repoSourceOf,
} from "@minga/protocol"
import { actorPart, type NamePart } from "@/features/project/who"
import { sinceText } from "@/model/glossary"

/**
 * Wave DOC (decision 0068, spec §5, §7): the pure parts of project documents in the web. Rows and folder groups of
 * Plan › Outputs › Project documents, kind labels, bylines with their time, the source link, a document page's
 * version, and the design-board runtime line. Over core (`documentHeads`, `docByline`, `viewOf`); unit-tested.
 */

/**
 * The kind label people see (mono caps). A Claude artifact link says so instead of LINK. A page (Markdown) has none
 * (onboarding week row 20, 0159 addendum): it is what a document usually is, so only the other kinds are named.
 */
export const DOC_KIND_LABEL: Readonly<Record<DocKind, string>> = {
  markdown: "",
  html: "HTML",
  image: "IMAGE",
  pdf: "PDF",
  text: "TEXT",
  link: "LINK",
  support: "SUPPORT FILE",
  file: "FILE",
}

/** Is this a Claude artifact link (the document's `doc.url`, or a link's own uri)? */
export const isClaudeLink = (a: Pick<Artifact, "uri" | "doc">): boolean => {
  const url = a.doc?.url ?? (docKindOf({ uri: a.uri, contentType: null }) === "link" ? a.uri : null)
  return url !== null && claudeArtifactOf(url) !== null
}

/** "" (a Markdown page), "HTML", "CLAUDE ARTIFACT" (a link to one), "LINK", … */
export const docKindLabel = (a: Pick<Artifact, "uri" | "contentType" | "doc">): string => {
  const kind = docKindOf(a)
  if (kind === "link" && isClaudeLink(a)) return "CLAUDE ARTIFACT"
  return DOC_KIND_LABEL[kind]
}

/**
 * "Jannik", "Lea": the one wording for names (UX pass phase 2: an agent by her name, no "(agent)"; her avatar and her
 * linked name say what she is). Minga for the rest.
 */
export const docNames =
  (s: Pick<ProjectState, "members" | "agents">) =>
  (actor: Actor): string =>
    actor._tag === "agent" || actor._tag === "worker" || actor._tag === "user"
      ? actorPart(s, actor).name
      : docActorName(s, actor)

/** "v3 · Updated by Lea · 2 h ago" (core `docByline` plus the time). */
export const docBylineAt = (s: ProjectState, a: Artifact, now: number): string =>
  `${docByline(s, a, docNames(s))} · ${sinceText(a.createdAt, now)}`

/**
 * The byline in parts (UX pass phase 2): the words before the name ("v3 · Updated by "), the name with whose page it
 * opens, and the rest (" · 2 h ago"), so the name links.
 */
export const docBylineParts = (
  s: ProjectState,
  a: Artifact,
  now: number,
): { readonly before: string; readonly name: NamePart; readonly after: string } => {
  const mark = "\u0000"
  const text = `${docByline(s, a, () => mark)} · ${sinceText(a.createdAt, now)}`
  const at = text.indexOf(mark)
  const name: NamePart =
    a.createdBy._tag === "agent" || a.createdBy._tag === "worker" || a.createdBy._tag === "user"
      ? actorPart(s, a.createdBy)
      : { name: docNames(s)(a.createdBy), who: null }
  return at < 0
    ? { before: text, name: { name: "", who: null }, after: "" }
    : { before: text.slice(0, at), name, after: text.slice(at + mark.length) }
}

/** The app path of a document's page (below the origin). */
export const docPagePath = (workspaceId: string, projectId: string, ref: string): string =>
  `/w/${encodeURIComponent(workspaceId)}/p/${encodeURIComponent(projectId)}/o/${encodeURIComponent(ref)}`

/** An app path as the full link people paste (row 25: "Copy link", not "Copy ref"). */
export const appLink = (path: string): string =>
  `${typeof window === "undefined" ? "https://app.minga.run" : window.location.origin}${path}`

/** The source link in a document's header: "Open on claude.ai ↗", "Open on GitHub ↗", or the host. */
export const sourceLink = (url: string | null): { readonly label: string; readonly href: string } | null => {
  if (url === null) return null
  const host = docUrlHost(url)
  if (host === null) return null
  if (claudeArtifactOf(url)) return { label: "Open on claude.ai ↗", href: url }
  if (repoSourceOf(url)) return { label: "Open on GitHub ↗", href: url }
  return { label: `Open on ${host} ↗`, href: url }
}

/** The repo is the document's home (a GitHub blob url): New version becomes "Edit in the repo, then re-import". */
export const isRepoHomed = (a: Pick<Artifact, "doc">): boolean =>
  a.doc !== undefined && docHomeOf(a.doc) === "external"

/**
 * May this person add, update, remove and purge documents? Any project member who is not a viewer (today's
 * `PostArtifact` rule for people, spec §9). Viewers read and download.
 */
export const canWriteDocs = (s: Pick<ProjectState, "members">, userId: string): boolean =>
  s.members.find((x) => x.userId === userId)?.role !== "viewer"

/** Is the actor an agent or a worker (an agent's version: "(agent)", scripts off)? */
export const byAgent = (a: Pick<Artifact, "createdBy">): boolean =>
  a.createdBy._tag === "agent" || a.createdBy._tag === "worker"

/**
 * The title people read (decision 0159, jargon row 22): a mandate page the pages job named "Mandate: @lea" before 0159
 * reads "Responsibilities: @lea"; every other title as stored.
 */
export const docTitle = (a: Pick<Artifact, "title">): string =>
  a.title.replace(/^Mandate: (@[\w.-]+)$/, "Responsibilities: $1")

/** One row of the documents section. */
export interface DocRow {
  readonly artifact: Artifact
  readonly ref: string | null
  readonly title: string
  readonly path: string | null
  readonly kind: DocKind
  readonly label: string
  /** Versions in the chain ("v3"). */
  readonly versions: number
  readonly byline: string
}

/**
 * The live documents people see in lists: every head but support files and `propose_page` drafts (`proposals/…`,
 * decision 0084: they are answered on their card), by path, path-less links last. `drafts` keeps the drafts.
 */
export const documentRows = (
  s: ProjectState,
  now: number,
  opts: { readonly drafts?: boolean } = {},
): ReadonlyArray<DocRow> =>
  documentHeads(s)
    .filter((a) => docKindOf(a) !== "support")
    .filter((a) => opts.drafts === true || !isProposalPath(docPathOf(a) ?? ""))
    .map((a) => ({
      artifact: a,
      ref: a.ref ?? null,
      title: docTitle(a),
      path: docPathOf(a),
      kind: docKindOf(a),
      label: docKindLabel(a),
      versions: versionsOf(s, a).length,
      byline: docBylineAt(s, a, now),
    }))

/** Rows whose title or path contains `q` (case-insensitive); all rows for an empty filter. */
export const filterDocRows = (rows: ReadonlyArray<DocRow>, q: string): ReadonlyArray<DocRow> => {
  const n = q.trim().toLowerCase()
  if (n === "") return rows
  return rows.filter((r) => r.title.toLowerCase().includes(n) || (r.path ?? "").toLowerCase().includes(n))
}

/** A folder of the documents section (or "Links": path-less links). */
export interface DocGroup {
  readonly key: string
  /** The folder ("docs/design/x"), "Top level" for the root, "Links" for path-less links. */
  readonly label: string
  /** The folder; "" for the root; null for "Links". */
  readonly folder: string | null
  /** The label is words ("Lea's pages", "Project pages"), not a raw folder path. */
  readonly named: boolean
  readonly rows: ReadonlyArray<DocRow>
  /** Support files (JS, CSS, fonts) under this folder that no deeper group shows: "+N support files". */
  readonly support: number
}

export const LINKS_GROUP = "Links"
export const ROOT_GROUP = "Top level"
export const PROJECT_PAGES = "Project pages"

/**
 * A folder in people's words (onboarding week rows 20 and 22, 0159 addendum): `project` is "Project pages",
 * `agents/<handle>` is "Lea's pages" (the agent's name from the state; "@x's pages" for a handle no agent has), a
 * folder below either keeps its rest ("Lea's pages / notes"). Null for every other folder (shown as its path).
 */
export const folderWords = (s: Pick<ProjectState, "agents">, folder: string): string | null => {
  const [top, handle, ...rest] = folder.split("/")
  const tail = (r: ReadonlyArray<string>) => (r.length > 0 ? ` / ${r.join("/")}` : "")
  if (top === "project") return `${PROJECT_PAGES}${tail(folder.split("/").slice(1))}`
  if (top === "agents" && handle) {
    const agent = s.agents.find((a) => a.handle.toLowerCase() === handle.toLowerCase())
    return `${agent ? agent.name : `@${handle}`}'s pages${tail(rest)}`
  }
  return null
}

/**
 * Rows grouped by folder (folders in path order), path-less links last under "Links". Each live support file counts
 * in the nearest group folder above it, so a folder with its HTML page shows "+3 support files".
 */
export const documentGroups = (s: ProjectState, rows: ReadonlyArray<DocRow>): ReadonlyArray<DocGroup> => {
  const byFolder = new Map<string, DocRow[]>()
  const links: DocRow[] = []
  for (const r of rows) {
    if (r.path === null) {
      links.push(r)
      continue
    }
    const f = docFolderOf(r.path)
    const key = docPathKey(f)
    const list = byFolder.get(key)
    if (list) list.push(r)
    else byFolder.set(key, [r])
  }
  const folders = [...byFolder.keys()].sort()
  const support = new Map<string, number>()
  for (const a of documentHeads(s, { kinds: ["support"] })) {
    const p = docPathOf(a)
    if (p === null) continue
    const own = docPathKey(docFolderOf(p))
    // the nearest group folder at or above the file's folder
    let best: string | null = null
    for (const f of folders)
      if ((f === "" || own === f || own.startsWith(`${f}/`)) && (best === null || f.length > best.length))
        best = f
    if (best !== null) support.set(best, (support.get(best) ?? 0) + 1)
  }
  const out: DocGroup[] = folders.map((key) => {
    const list = byFolder.get(key) ?? []
    const folder = list[0]?.path ? docFolderOf(list[0].path) : key
    const words = folder === "" ? null : folderWords(s, folder)
    return {
      key: `f:${key}`,
      label: folder === "" ? ROOT_GROUP : (words ?? folder),
      folder,
      named: words !== null,
      rows: list,
      support: support.get(key) ?? 0,
    }
  })
  if (links.length > 0)
    out.push({ key: "links", label: LINKS_GROUP, folder: null, named: true, rows: links, support: 0 })
  return out
}

/** "+3 support files", "+1 support file"; null for none. */
export const supportLine = (n: number): string | null =>
  n <= 0 ? null : `+${n} support file${n === 1 ? "" : "s"}`

/** "12 documents", "1 document". */
export const documentCount = (n: number): string => `${n} document${n === 1 ? "" : "s"}`

/** A document page's subject: the chain's head, its versions (oldest first) and the version shown (`?v=<n>`). */
export interface DocPageSubject {
  readonly head: Artifact
  readonly versions: ReadonlyArray<Artifact>
  readonly shown: Artifact
  /** 1-based place of `shown` in `versions`. */
  readonly version: number
  /** `shown` is the head (the version people get by its ref). */
  readonly isHead: boolean
  /** A document (`doc` set); false for an output opened by its `O-n`. */
  readonly document: boolean
}

/** The page of `ref` (`O-14`, any case), showing version `v` (1-based; the head when absent or out of range). */
export const docPageSubject = (
  s: Pick<ProjectState, "artifacts">,
  ref: string,
  v?: number | undefined,
): DocPageSubject | null => {
  const head = artifactByRef(s, ref)
  if (!head) return null
  const versions = versionsOf(s, head)
  const at = v !== undefined && Number.isInteger(v) && v >= 1 && v <= versions.length ? v : versions.length
  const shown = versions[at - 1] ?? head
  return {
    head,
    versions,
    shown,
    version: at,
    isHead: shown.id === head.id,
    document: isDocument(head),
  }
}

/** "v3 of 3". */
export const versionLabel = (version: number, total: number): string => `v${version} of ${total}`

/** A design board (`.dc.html`) whose view has no `support.js` renders its static layout: the page says so. */
export const BOARD_RUNTIME_LINE =
  "This design board is shown without its scripts, so placeholders and every variant appear."

/** The live board's link under {@link BOARD_RUNTIME_LINE}: only when the document's source is a Claude artifact. */
export const liveBoardLink = (a: Pick<Artifact, "doc">): string | null => {
  const url = a.doc?.url ?? null
  return url !== null && claudeArtifactOf(url) !== null ? url : null
}

/** Does this version of an HTML document need {@link BOARD_RUNTIME_LINE}? */
export const boardWithoutRuntime = (s: ProjectState, a: Artifact): boolean => {
  const path = docPathOf(a)
  if (path === null || !/\.dc\.html?$/i.test(path)) return false
  const view = viewOf(s, a)
  if (!view) return false
  return !view.files.some((f) => /(^|\/)support\.js$/i.test(f.path))
}

/** The search params of the document route (`?v=<n>`). */
export interface DocumentSearch {
  v?: number
}

/** `?v=` is a positive integer, else dropped. */
export const validateDocumentSearch = (search: Record<string, unknown>): DocumentSearch => {
  const raw = search.v
  const n =
    typeof raw === "number" ? raw : typeof raw === "string" && /^\d{1,6}$/.test(raw) ? Number(raw) : NaN
  return Number.isInteger(n) && n >= 1 ? { v: n } : {}
}
